import { useEffect, useState, useRef } from 'react';
import isEqual from 'lodash.isequal';
import IconDown from '../icons/down';
import IconUp from '../icons/up';
import CheckboxInput from '../checkbox/checkbox';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import { Filters, SavedFilterTypes, SchemaFilterType } from './types';
import FilterTag from '../tags/filter';
import useSelectedTags from './hooks/use-selected-tags';
import { SelectItemProps } from '../select/types';
import ButtonSecondary from '../button/button-secondary';
import ButtonPseudo from '../button/button-pseudo';
import { ButtonSize } from '../button/types';
import IconInfo from '../icons/info';
import usePrevious from '../../hooks/use-previous';

import './table.css';

export default function SavedFilter({
  item,
  selectedSavedFilter,
  onSavedFilterClick,
  onFilterChange,
  filters,
  setSelectedSavedFilter,
  columnsLabels,
  lists,
  savedFilters,
  setSavedFilters,
  savedFiltersKey,
  itemIndex,
  userId,
}: SavedFilterTypes) {
  const [collapsed, setCollapsed] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [values, setValues] = useState<Filters>({});
  const [filtersLists, setFiltersList] =
    useState<Record<string, SelectItemProps[]>>();
  const [notFullVisible, setNotFullVisible] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const prevCollapsed = usePrevious(collapsed);

  const { selectedOptionsTags } = useSelectedTags(values, columnsLabels);

  const isFullyVisible = (
    childDiv: HTMLDivElement,
    parentContainer: HTMLDivElement,
  ) => {
    const childRect = childDiv.getBoundingClientRect();
    const parentRect = parentContainer.getBoundingClientRect();

    return (
      childRect.top >= parentRect.top &&
      childRect.left >= parentRect.left &&
      childRect.bottom <= parentRect.bottom &&
      childRect.right <= parentRect.right
    );
  };

  const handleRemoveFilterTag = (fieldName: string, value: string) => {
    const nextFilters = { ...values };

    Object.entries(values).forEach(([key]) => {
      if (key === fieldName) {
        if (nextFilters[key].type !== SchemaFilterType.DATE_RANGE) {
          nextFilters[key] = {
            value: values[key]?.value?.filter(
              (itemValue) => itemValue !== value,
            ),
            type: nextFilters[key].type,
          };
        } else {
          delete nextFilters[key];
        }
      }
    });

    setValues(nextFilters);
  };

  const onDeleteSavedFilters = () => {
    if (selectedSavedFilter === item?.id) {
      if (setSelectedSavedFilter) {
        setSelectedSavedFilter('');
      }

      onFilterChange({
        cursor: filters?.cursor,
      });
    }

    setCollapsed(false);

    const savedFiltersJSON = localStorage.getItem(savedFiltersKey as string);

    if (savedFiltersJSON) {
      const parsed: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[] = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const nextSavedFilters = parsed
          ?.filter((filterItem) => filterItem.id !== item?.id)
          ?.map((filterItem, index) => ({
            ...filterItem,
            id: `${savedFiltersKey}-${index + 1}`,
          }));

        const currentUserSavedFilters = nextSavedFilters?.filter(
          (filterItem) => filterItem.employeeId === userId,
        );

        if (setSavedFilters) {
          setSavedFilters(
            currentUserSavedFilters,
          );
        }

        if (nextSavedFilters?.length) {
          localStorage.setItem(
            savedFiltersKey as string,
            JSON.stringify(nextSavedFilters),
          );
        } else {
          localStorage.removeItem(savedFiltersKey as string);
        }
      }
    }
  };

  const onSaveFilter = () => {
    const savedFiltersJSON = localStorage.getItem(savedFiltersKey as string);

    if (savedFiltersJSON) {
      const parsed: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
        employeeId: string;
      }[] = JSON.parse(savedFiltersJSON);

      const nextSavedFilters = savedFilters?.map((savedFilter, index) => {
        const result = { ...savedFilter };

        result.default = isDefault;

        if (result.employeeId === userId) {
          if (isDefault) {
            if (index === itemIndex) {
              result.default = isDefault;
            } else {
              result.default = false;
            }
          }

          if (index === itemIndex) {
            result.value = values;
          }
        }

        return result;
      });

      setCollapsed(false);

      if (setSavedFilters) {
        setSavedFilters(
          nextSavedFilters as {
            id: string;
            name: string;
            value: Record<string, any>;
            default?: boolean;
            employeeId: string;
          }[],
        );
      }

      if (onSavedFilterClick) {
        onSavedFilterClick(
          item?.id as string,
          values,
        );
      }

      if (parsed) {
        const nextParsedSavedFilters = parsed?.map((filterItem) => {
          let result = { ...filterItem };

          const currentUpdatedFilterValue = nextSavedFilters?.find(
            (savedFilterItem) => savedFilterItem.id === result.id,
          );

          if (currentUpdatedFilterValue) {
            result = currentUpdatedFilterValue;
          }

          return result;
        });

        localStorage.setItem(
          savedFiltersKey as string,
          JSON.stringify(nextParsedSavedFilters),
        );
      }
    }
  };

  const handleCloseFilter = () => {
    setNotFullVisible(false);

    if (item) {
      setValues(item?.value);

      setIsDefault(item?.default as boolean);
    }
  };

  useEffect(() => {
    if (item) {
      setValues(item.value);

      setIsDefault(item?.default as boolean);
    }
  }, [item]);

  useEffect(() => {
    if (lists) {
      setFiltersList(lists);
    }
  }, [lists]);

  useEffect(() => {
    if (collapsed && !prevCollapsed) {
      handleCloseFilter();
    }
  }, [collapsed, prevCollapsed]);

  useEffect(() => {
    if (collapsed) {
      setTimeout(() => {
        if (menuRef?.current) {
          const parent = document.getElementById('layout-right');

          if (!isFullyVisible(menuRef?.current, parent as HTMLDivElement)) {
            setNotFullVisible(true);
          }
        }
      }, 1);
    }
  }, [menuRef, collapsed]);

  return (
    <div
       
      className={`saved-filter-button ${
        selectedSavedFilter === item?.id ? 'active' : ''
      } ${collapsed ? 'with-z-index' : ''}`}
    >
      <CheckboxInput
        title={item?.name}
        type="checkbox"
        name={`checkbox-${item?.id}`}
        checked={selectedSavedFilter === item?.id}
        visualClassName="saved-filter-checkbox"
        onChange={(e) => {
          if (onSavedFilterClick) {
            if (e.target.checked) {
              onSavedFilterClick(
                item?.id as string,
                item?.value as Record<string, any>,
              );
            } else {
              onFilterChange({
                cursor: filters?.cursor,
              });

              if (setSelectedSavedFilter) {
                setSelectedSavedFilter('');
              }
            }
          }
        }}
      />
      <div className="saved-filter-button-separator" />
      <ClickOutsideDetector setOptionsListVisible={setCollapsed}>
        <>
          <button
            type="button"
            className="show-filter-values"
            aria-label="Show filter values"
            onClick={() => {
              if (!collapsed) {
                setCollapsed(true);
              } else {
                setCollapsed(false);
                setNotFullVisible(false);

                if (item) {
                  setValues(item?.value);

                  if (item.default) {
                    setIsDefault(item?.default);
                  }
                }
              }
            }}
          >
            {collapsed ? <IconUp /> : <IconDown />}
          </button>
          {collapsed && (
            <div
              className="saved-filters-values-wrapper"
              ref={menuRef}
              style={{
                right: notFullVisible ? '0' : 'inherit',
                left: notFullVisible ? 'initial' : '0',
              }}
            >
              <div className="saved-filters-values-top">
                <div className="saved-filters-values-top-title">
                  Preset filters
                </div>
                <div className="saved-filters-values-top-right">
                  <CheckboxInput
                    title="Default filters"
                    type="checkbox"
                    checked={isDefault}
                    name={`default-${item?.id}`}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  <div className="default-filters-hint">
                    <IconInfo width={18} height={18} />
                  </div>
                </div>
              </div>
              <div className="saved-filters-values">
                {selectedOptionsTags?.map((filterItem) => {
                  let name = filterItem.value;

                  if (filtersLists) {
                    const currentList = filtersLists[filterItem.key];

                    if (currentList) {
                      const currentItem = currentList.find(
                        (listItem) => listItem.value === filterItem.value,
                      );

                      if (currentItem) {
                        name = currentItem.name;

                        if (currentItem.suffix) {
                          name = `${name} (${currentItem.suffix})`;
                        }
                      }
                    }
                  }

                  return (
                    <div
                      className="saved-filter-value-wrapper"
                      key={`${filterItem.key}-${filterItem.value}`}
                    >
                      <FilterTag
                        type="round"
                        title={
                          <>
                            <span className="saved-filter-values-label">{`${
                              filterItem?.label as string
                            }: `}</span>
                            <span>{name}</span>
                          </>
                        }
                        onClick={() =>
                          handleRemoveFilterTag(
                            filterItem.key,
                            filterItem.value,
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="saved-filters-values-controls">
                {(!isEqual(isDefault, item?.default) ||
                  !isEqual(values, item?.value)) && (
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    text="Save changes"
                    className="save-changes-button"
                    onClick={onSaveFilter}
                  />
                )}
                <ButtonPseudo
                  size={ButtonSize.SMALL}
                  text="Delete preset"
                  onClick={onDeleteSavedFilters}
                />
              </div>
            </div>
          )}
        </>
      </ClickOutsideDetector>
    </div>
  );
}
